import type { TStoreName } from "../types/store";
import { buildDocument } from "./build-document";
import { APP_NAME, STORE_SUFFIX } from "./store";

export const customerCreateMutation = buildDocument(`#graphql
  mutation CustomerCreate($input: CustomerCreateInput!) {
    customerCreate(input: $input) {
      customer { ...CustomerBaseFields }
      customerUserErrors { ...CustomerUserErrorFields }
      userErrors { ...UserErrorFields }
    }
  }
`);

export const customerAccessTokenCreateMutation = buildDocument(`#graphql
  mutation CustomerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {
    customerAccessTokenCreate(input: $input) {
      customerAccessToken { ...CustomerAccessTokenFields }
      customerUserErrors { ...CustomerUserErrorFields }
    }
  }
`);

export const customerRecoverMutation = buildDocument(`#graphql
  mutation CustomerRecover($email: String!) {
    customerRecover(email: $email) {
      customerUserErrors { ...CustomerUserErrorFields }
    }
  }
`);

/** 비밀번호 재설정 메일의 링크(/account/reset/:id/:resetToken)로 들어왔을 때 */
export const customerResetMutation = buildDocument(`#graphql
  mutation CustomerReset($id: ID!, $input: CustomerResetInput!) {
    customerReset(id: $id, input: $input) {
      customerAccessToken { ...CustomerAccessTokenFields }
      customerUserErrors { ...CustomerUserErrorFields }
    }
  }
`);

/** 계정 활성화 메일의 링크(/account/activate/:id/:activationToken)로 들어왔을 때 */
export const customerActivateMutation = buildDocument(`#graphql
  mutation CustomerActivate($id: ID!, $input: CustomerActivateInput!) {
    customerActivate(id: $id, input: $input) {
      customerAccessToken { ...CustomerAccessTokenFields }
      customerUserErrors { ...CustomerUserErrorFields }
    }
  }
`);

export const customerAddressCreateMutation = buildDocument(`#graphql
  mutation CustomerAddressCreate($address: MailingAddressInput!, $customerAccessToken: String!) {
    customerAddressCreate(address: $address, customerAccessToken: $customerAccessToken) {
      customerAddress { ...MailingAddressFields }
      customerUserErrors { ...CustomerUserErrorFields }
    }
  }
`);

export const customerAddressDeleteMutation = buildDocument(`#graphql
  mutation CustomerAddressDelete($customerAccessToken: String!, $id: ID!) {
    customerAddressDelete(customerAccessToken: $customerAccessToken, id: $id) {
      deletedCustomerAddressId
      customerUserErrors { ...CustomerUserErrorFields }
    }
  }
`);

export const customerAddressUpdateMutation = buildDocument(`#graphql
  mutation CustomerAddressUpdate($address: MailingAddressInput!, $customerAccessToken: String!, $id: ID!) {
    customerAddressUpdate(address: $address, customerAccessToken: $customerAccessToken, id: $id) {
      customerAddress { ...MailingAddressFields }
      customerUserErrors { ...CustomerUserErrorFields }
    }
  }
`);

export const customerDefaultAddressUpdateMutation = buildDocument(`#graphql
  mutation CustomerDefaultAddressUpdate($addressId: ID!, $customerAccessToken: String!) {
    customerDefaultAddressUpdate(addressId: $addressId, customerAccessToken: $customerAccessToken) {
      customer { ...CustomerBaseFields }
      customerUserErrors { ...CustomerUserErrorFields }
    }
  }
`);

/* ------------------------------------------------------------------ *
 * 회원 조회 / 주문 목록 (주문 fragment 사용)
 * ------------------------------------------------------------------ */

/** 스토어별로 다른 쿼리만 키로 두고, 나머지는 default 에 `${S}` 보간으로 한 벌만 유지한다. */
const withFragments = (operations: Partial<Record<TStoreName, string>> & { default: string }) =>
  buildDocument(operations[APP_NAME] ?? operations.default);

const S = STORE_SUFFIX;

/** 회원 정보 조회 */
/** 로그인 사용자의 원격 cartId 조회 (customer metafield info.cart_id) */
export const customerCartIdQuery = withFragments({
  default: `#graphql
    query CustomerCartId${S}($customerAccessToken: String!) {
      customer(customerAccessToken: $customerAccessToken) {
        id
        cartId: metafield(namespace: "info", key: "cart_id") { ...MetafieldFields }
      }
    }
  `,
});

export const customerQuery = withFragments({
  default: `#graphql
    query Customer${S}($customerAccessToken: String!) {
      customer(customerAccessToken: $customerAccessToken) {
        ...CustomerProfileFields${S}
        orders(first: 250, sortKey: PROCESSED_AT, reverse: true) {
          edges { node { ...CustomerOrderFields${S} } }
        }
      }
    }
  `,
});
/** 회원 정보 수정 */
export const customerUpdateMutation = withFragments({
  default: `#graphql
    mutation CustomerUpdate${S}($customer: CustomerUpdateInput!, $customerAccessToken: String!) {
      customerUpdate(customer: $customer, customerAccessToken: $customerAccessToken) {
        customer {
          ...CustomerProfileFields${S}
          orders(first: 250, sortKey: PROCESSED_AT, reverse: true) {
            edges { node { ...CustomerOrderFields${S} } }
          }
        }
        customerAccessToken { ...CustomerAccessTokenFields }
        customerUserErrors { ...CustomerUserErrorFields }
        userErrors { ...UserErrorFields }
      }
    }
  `,
});
/** 주문 목록 */
export const customerOrdersQuery = withFragments({
  default: `#graphql
    query CustomerOrder${S}($customerAccessToken: String!, $first: Int, $sortKey: OrderSortKeys, $reverse: Boolean, $after: String) {
      customer(customerAccessToken: $customerAccessToken) {
        orders(first: $first, sortKey: $sortKey, reverse: $reverse, after: $after) {
          edges { node { ...CustomerOrderFields${S} } }
          pageInfo { ...PageInfoFields }
        }
      }
    }
  `,
});
/** 총 결제 금액 */
export const totalPaidAmountQuery = withFragments({
  default: `#graphql
    query TotalPaidAmount${S}($customerAccessToken: String!, $first: Int, $sortKey: OrderSortKeys, $reverse: Boolean, $after: String, $query: String) {
      customer(customerAccessToken: $customerAccessToken) {
        email
        orders(first: $first, sortKey: $sortKey, reverse: $reverse, after: $after, query: $query) {
          edges {
            node {
              ...OrderAmountFields
              isfamilysale: metafield(namespace: "operation", key: "isfamilysale") { ...MetafieldFields }
            }
          }
          pageInfo { ...PageInfoFields }
        }
      }
    }
  `,
});
